import type { Channel } from './get-channels-from-pypes';

/**
 * Call Recording (PSTUDIO-4090).
 *
 * The setting is per Voice AI channel: it lives in that channel's
 * `twilio_params.enable_call_recording` and is edited on the channel's own
 * Voice AI Settings page. There is deliberately no project-wide control —
 * Engagement has no notion of a project, and CS asked for per-channel control.
 */

/**
 * Whether a channel records.
 *
 * The contract, confirmed by Eduardo on PSTUDIO-4090: recording is on when the
 * stored value is true, and off for *every* other case — the key missing, null,
 * or undefined. So this is not a truthiness check. The Channels API round-trips
 * an absent value as the STRING `'undefined'` or `'null'`, and both are truthy,
 * so reading the field directly would show a channel nobody configured as
 * recording. Only a real boolean `true`, or a string spelling it, counts.
 *
 * The compare is case-insensitive because the value crosses services and we do
 * not control which one writes it: a Python producer spells the same boolean
 * `'True'`, and treating that as off would turn recording off on this channel's
 * next save of any unrelated field — the exact defect this work exists to fix.
 */
export const isCallRecordingEnabled = (
  channel: Channel | null | undefined
): boolean => {
  const value = channel?.twilioVoiceConfig?.enable_call_recording;
  if (typeof value === 'boolean') return value;
  return typeof value === 'string' && value.toLowerCase() === 'true';
};
